// src/pages/seasons/CreateSeason.jsx
'use client';

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import { useTeam } from '@/context/TeamContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import clsx from 'clsx';

import { Input } from '@/components/input';
import { Textarea } from '@/components/textarea';
import { Button } from '@/components/button';
import { track, ACTIVATION_EVENTS } from '@/lib/analytics';
import { Field, Label as CatalystLabel, ErrorMessage, Description } from '@/components/fieldset';
import { Heading, Subheading } from '@/components/heading';
import { Text } from '@/components/text';
import {
  validatePricingTiers,
  suggestProratedTiers,
  formatTierPrice,
} from '@/utils/pricingTiers';
import { toSeasonDate } from '@/utils/seasonDates';

import {
  ArrowPathIcon,
  PlusIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  TagIcon,
  SparklesIcon,
  HeartIcon,
  CreditCardIcon,
  TrashIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// Shared with the auto-default season so the two can never drift
// (src/utils/defaultSeason.js is the single source of the sample text).
import { DEFAULT_WAIVER, seasonAutoNameParts } from '@/utils/defaultSeason';

let _divCounter = 0;
const uid = () => `div_${++_divCounter}_${Math.random().toString(36).slice(2, 6)}`;

/* ── Toggle helper ──────────────────────────────────── */
function Toggle({ enabled, onToggle, label, description }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={onToggle}
        className={clsx(
          'relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#b5f03d]/60 focus:ring-offset-1',
          enabled ? 'bg-[#5a8a1f]' : 'bg-zinc-300'
        )}
      >
        <span
          className={clsx(
            'absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
            enabled ? 'translate-x-4' : 'translate-x-0'
          )}
        />
      </button>
      <div>
        <p className="text-sm font-medium text-zinc-900">{label}</p>
        {description && <p className="text-xs text-zinc-500 mt-0.5">{description}</p>}
      </div>
    </label>
  );
}

export default function CreateSeason() {
  const navigate = useNavigate();
  const { firebaseUser } = useAuth();
  const { currentTeam } = useTeam();
  const { t } = useTranslation();

  /* ── Core form state ────────────────────────────── */
  // Sensible defaults (#6, funnel-walk): registration opens TODAY so the
  // link a coach shares right after creating works without them thinking
  // about dates at all. Everything stays editable.
  const localYMD = (offsetDays = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return d.getFullYear() + '-' + mm + '-' + dd;
  };
  const [form, setForm] = useState(() => ({
    name: '',
    registrationOpen: localYMD(0),
    registrationClose: localYMD(30),
    seasonStart: localYMD(0),
    seasonEnd: localYMD(120),
    maxPlayers: '',
    registrationFee: '',
  }));

  /* ── Pricing config state ───────────────────────── */
  const [pricingMode,       setPricingMode]       = useState('flat'); // 'flat' | 'divisions' | 'tiered'
  const [divisionRows,      setDivisionRows]      = useState([{ id: uid(), name: '', fee: '' }]);

  // Tiered pricing — time-based registration windows (Early Bird → Regular → Late...)
  // Each row mirrors the schema in src/utils/pricingTiers.js but stores user input as
  // strings (price, dates) so the form can render them; we coerce on submit.
  const [tierRows, setTierRows] = useState(() => [
    { id: uid(), name: t('createSeason.pricing.tierEarlyBird'), price: '', startsAt: '', endsAt: '', maxRegistrations: '' },
    { id: uid(), name: t('createSeason.pricing.tierRegular'),   price: '', startsAt: '', endsAt: '', maxRegistrations: '' },
  ]);
  const [calcOpen,        setCalcOpen]        = useState(false);
  const [calcFullPrice,   setCalcFullPrice]   = useState('');
  const [calcTierCount,   setCalcTierCount]   = useState(3);

  const [earlyBirdEnabled,  setEarlyBirdEnabled]  = useState(false);
  const [earlyBirdDiscount, setEarlyBirdDiscount] = useState('');
  const [earlyBirdDeadline, setEarlyBirdDeadline] = useState('');

  const [siblingEnabled,    setSiblingEnabled]    = useState(false);
  const [siblingAmount,     setSiblingAmount]     = useState('');

  const [returningEnabled,  setReturningEnabled]  = useState(false);
  const [returningAmount,   setReturningAmount]   = useState('');

  const [planEnabled,       setPlanEnabled]       = useState(false);
  const [planInstallments,  setPlanInstallments]  = useState(4);            // 2–12
  const [planFrequency,     setPlanFrequency]     = useState('monthly');     // 'weekly' | 'biweekly' | 'monthly'
  const [planChargeDay,     setPlanChargeDay]     = useState(15);            // 1–28 (monthly only)

  // Processing fee: 'absorb' (coach pays Stripe fee) | 'pass_to_parent' (parent pays)
  const [processingFeeMode, setProcessingFeeMode] = useState('pass_to_parent');

  const [aidEnabled,        setAidEnabled]        = useState(false);

  // A flat-fee season with no fee (or fee 0) is FREE: every money surface
  // (discounts, payment plans, processing fees, what-the-fee-covers) is
  // hidden AND inert, even if a coach toggled something before zeroing
  // the fee (#5, funnel-walk finding).
  const feeIsFree =
    pricingMode === 'flat' &&
    (!form.registrationFee || Number(form.registrationFee) === 0);
  const ebOn  = earlyBirdEnabled && !feeIsFree;
  const sibOn = siblingEnabled   && !feeIsFree;
  const retOn = returningEnabled && !feeIsFree;
  const plOn  = planEnabled      && !feeIsFree;
  const finAidOn = aidEnabled    && !feeIsFree;
  const [openNow,           setOpenNow]           = useState(true);

  /* ── Waivers ─────────────────────────────────────────── */
  // 2026-07-24 (founder decision): Scrimly does not author waivers.
  // Seasons start with NO waivers; coaches add their own text or opt
  // into a sample preset (with the examples-only disclaimer) — their
  // legal call, not ours.
  const [waivers, setWaivers] = useState([]);
  const addWaiver = (preset = '') => {
    const presetTexts = {
      'Liability Waiver': DEFAULT_WAIVER,
      'Concussion Protocol': 'CONCUSSION PROTOCOL\n\nA concussion is a brain injury that can be serious. A student-athlete who shows or reports one or more of the signs/symptoms of a concussion shall be removed from play and shall not return to play until cleared by an appropriate health care professional.\n\nBy signing below, I acknowledge that I have read and understand this concussion information.',
      'Social Media Policy': 'SOCIAL MEDIA CONDUCT POLICY\n\nAll players, parents, and guardians agree to represent the team positively on all social media platforms. This includes refraining from posting negative comments about officials, opposing teams, or team members. Violations may result in suspension from team activities.',
      'Photo Release': 'PHOTO & VIDEO RELEASE\n\nI hereby grant permission to use photographs and video recordings of my child taken during team activities for promotional and educational purposes, including on the team website and social media channels.',
    };
    setWaivers(w => [...w, { id: uid(), name: preset || '', text: presetTexts[preset] || '' }]);
  };

  const removeWaiver = (id) => setWaivers(w => w.filter(x => x.id !== id));
  const updateWaiver = (id, field, val) => setWaivers(w => w.map(x => x.id === id ? { ...x, [field]: val } : x));

  /* ── Required Documents ─────────────────────────────── */
  const DOC_OPTIONS = [
    { key: 'birth_certificate', label: t('createSeason.docs.birth_certificate_label'), icon: '🪪', hint: t('createSeason.docs.birth_certificate_hint') },
    { key: 'player_photo',      label: t('createSeason.docs.player_photo_label'),      icon: '📷', hint: t('createSeason.docs.player_photo_hint') },
    { key: 'club_registration', label: t('createSeason.docs.club_registration_label'), icon: '📋', hint: t('createSeason.docs.club_registration_hint') },
    { key: 'medical_form',      label: t('createSeason.docs.medical_form_label'),      icon: '🩺', hint: t('createSeason.docs.medical_form_hint') },
    { key: 'insurance_card',    label: t('createSeason.docs.insurance_card_label'),    icon: '🛡️', hint: t('createSeason.docs.insurance_card_hint') },
  ];
  const [requiredDocs, setRequiredDocs] = useState([]);
  const toggleDoc = (key) => setRequiredDocs(prev =>
    prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
  );

  /* ── What's Included ────────────────────────────────── */
  const [whatsIncluded, setWhatsIncluded] = useState(() => [
    t('createSeason.included.default1'),
    t('createSeason.included.default2'),
    t('createSeason.included.default3'),
    t('createSeason.included.default4'),
  ]);
  const addIncludedItem    = () => setWhatsIncluded(p => [...p, '']);
  const removeIncludedItem = (i) => setWhatsIncluded(p => p.filter((_, idx) => idx !== i));
  const updateIncludedItem = (i, val) => setWhatsIncluded(p => p.map((item, idx) => idx === i ? val : item));

  /* ── UI state ───────────────────────────────────── */
  const [saving,    setSaving]    = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [errors,    setErrors]    = useState({});

  /* ── Division helpers ───────────────────────────── */
  const addDivision    = () => setDivisionRows(r => [...r, { id: uid(), name: '', fee: '' }]);
  const removeDivision = (id) => setDivisionRows(r => r.filter(d => d.id !== id));
  const updateDivision = (id, field, value) =>
    setDivisionRows(r => r.map(d => d.id === id ? { ...d, [field]: value } : d));

  /* ── Tier helpers ───────────────────────────────── */
  const addTier = () => setTierRows(r => [
    ...r,
    { id: uid(), name: t('createSeason.pricing.tierN', { n: r.length + 1 }), price: '', startsAt: '', endsAt: '', maxRegistrations: '' },
  ]);
  const removeTier = (id) => setTierRows(r => r.filter(t => t.id !== id));
  const updateTier = (id, field, value) =>
    setTierRows(r => r.map(t => t.id === id ? { ...t, [field]: value } : t));

  // Apply prorated tier suggestions from the calculator. Replaces the current tier list.
  const applyCalculator = () => {
    const fullCents = Math.round(Number(calcFullPrice) * 100);
    if (!fullCents || fullCents <= 0) {
      toast.error(t('createSeason.toast.calcEnterPrice'));
      return;
    }
    if (!form.seasonStart || !form.seasonEnd) {
      toast.error(t('createSeason.toast.calcSetDates'));
      return;
    }
    const suggestions = suggestProratedTiers({
      fullPriceInCents: fullCents,
      seasonStart: toSeasonDate(form.seasonStart),
      seasonEnd:   toSeasonDate(form.seasonEnd),
      tierCount:   Number(calcTierCount),
    });
    if (!suggestions.length) {
      toast.error(t('createSeason.toast.calcCantGenerate'));
      return;
    }
    const toDateInput = (d) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    setTierRows(suggestions.map((s, i) => ({
      id: uid(),
      name: s.name,
      price: (s.priceInCents / 100).toFixed(2),
      startsAt: toDateInput(s.startsAt),
      endsAt:   toDateInput(s.endsAt),
      maxRegistrations: '',
    })));
    setCalcOpen(false);
    toast.success(t('createSeason.toast.calcGenerated', { count: suggestions.length }));
  };

  /* ── Handlers ───────────────────────────────────── */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: null }));
  }, [errors]);

  /* ── Validation ─────────────────────────────────── */
  const validate = useCallback(() => {
    const e = {};
    if (!form.name.trim()) e.name = t('createSeason.err.seasonName');

    // Pricing
    if (pricingMode === 'flat') {
      if (form.registrationFee === '' || isNaN(Number(form.registrationFee)) || Number(form.registrationFee) < 0)
        e.registrationFee = t('createSeason.err.fee');
    } else if (pricingMode === 'divisions') {
      const valid = divisionRows.filter(d => d.name.trim() && d.fee !== '' && !isNaN(Number(d.fee)) && Number(d.fee) >= 0);
      if (valid.length === 0) e.divisions = t('createSeason.err.divisions');
    } else if (pricingMode === 'tiered') {
      // Coerce rows into the schema shape and run the canonical validator.
      const coerced = tierRows.map((t, i) => ({
        id: t.id,
        name: t.name,
        priceInCents: t.price === '' ? NaN : Math.round(Number(t.price) * 100),
        startsAt: toSeasonDate(t.startsAt),
        endsAt:   toSeasonDate(t.endsAt),
        maxRegistrations: t.maxRegistrations === '' ? null : Number(t.maxRegistrations),
        sortOrder: i,
      }));
      const result = validatePricingTiers(coerced);
      if (!result.ok) e.tiers = result.errors.join(' ');
    }

    // Dates
    if (!form.registrationOpen)  e.registrationOpen  = t('createSeason.err.required');
    if (!form.registrationClose) e.registrationClose = t('createSeason.err.required');
    if (form.registrationOpen && form.registrationClose && form.registrationClose <= form.registrationOpen)
      e.registrationClose = t('createSeason.err.closeAfterOpen');
    if (!form.seasonStart) e.seasonStart = t('createSeason.err.required');
    if (!form.seasonEnd)   e.seasonEnd   = t('createSeason.err.required');
    if (form.seasonStart && form.seasonEnd && form.seasonEnd <= form.seasonStart)
      e.seasonEnd = t('createSeason.err.endAfterStart');

    // Waivers
    const validWaivers = waivers.filter(w => w.name.trim() && w.text.trim());
    if (validWaivers.length === 0) e.waivers = t('createSeason.err.waiverRequired');

    // Max players
    if (form.maxPlayers && (isNaN(Number(form.maxPlayers)) || Number(form.maxPlayers) < 1))
      e.maxPlayers = t('createSeason.err.maxPlayers');

    // Early bird (skipped in tiered mode — tiers ARE the time-based discount)
    if (ebOn && pricingMode !== 'tiered') {
      if (!earlyBirdDeadline) e.earlyBirdDeadline = t('createSeason.err.required');
      if (!earlyBirdDiscount || isNaN(Number(earlyBirdDiscount)) || Number(earlyBirdDiscount) <= 0)
        e.earlyBirdDiscount = t('createSeason.err.earlyBirdDiscount');
    }

    // Sibling discount
    if (sibOn && (!siblingAmount || isNaN(Number(siblingAmount)) || Number(siblingAmount) <= 0))
      e.siblingAmount = t('createSeason.err.discountAmount');

    // Returning discount
    if (retOn && (!returningAmount || isNaN(Number(returningAmount)) || Number(returningAmount) <= 0))
      e.returningAmount = t('createSeason.err.discountAmount');

    // Payment plan
    if (plOn) {
      const n = Number(planInstallments);
      if (!Number.isFinite(n) || n < 2 || n > 12)
        e.planInstallments = t('createSeason.err.installments');
      if (planFrequency === 'monthly') {
        const d = Number(planChargeDay);
        if (!Number.isFinite(d) || d < 1 || d > 28)
          e.planChargeDay = t('createSeason.err.chargeDay');
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  }, [form, pricingMode, divisionRows, tierRows, feeIsFree, ebOn, sibOn, retOn, plOn, earlyBirdDiscount, earlyBirdDeadline,
      siblingEnabled, siblingAmount, returningEnabled, returningAmount,
      planEnabled, planInstallments, planFrequency, planChargeDay, waivers, t]);

  /* ── Submit ─────────────────────────────────────── */
  const onSubmit = async (e) => {
    e.preventDefault();
    setAttempted(true);
    if (!validate()) { toast.error(t('createSeason.toast.fixErrors')); return; }
    if (!currentTeam?.id) { toast.error(t('createSeason.toast.noTeam')); return; }

    setSaving(true);
    const tid = toast.loading(t('createSeason.toast.creating'));
    try {
      // Build divisionPricing map
      let divisionPricing = null;
      let pricingTiers    = null;
      let registrationFee;

      if (pricingMode === 'divisions') {
        divisionPricing = {};
        divisionRows
          .filter(d => d.name.trim() && d.fee !== '')
          .forEach(d => { divisionPricing[d.name.trim()] = Math.round(Number(d.fee) * 100); });
        registrationFee = Math.min(...Object.values(divisionPricing)); // min for fallback/display
      } else if (pricingMode === 'tiered') {
        pricingTiers = tierRows.map((t, i) => ({
          id:               t.id,
          name:             t.name.trim(),
          priceInCents:     Math.round(Number(t.price) * 100),
          startsAt:         toSeasonDate(t.startsAt),
          endsAt:           toSeasonDate(t.endsAt),
          maxRegistrations: t.maxRegistrations === '' ? null : Number(t.maxRegistrations),
          sortOrder:        i,
        }));
        // For backward-compat displays (cards, etc.) use the highest tier price as the
        // headline. The actual price the parent pays is resolved at checkout via
        // getActivePricingTier().
        registrationFee = Math.max(...pricingTiers.map(t => t.priceInCents));
      } else {
        registrationFee = Math.round(Number(form.registrationFee) * 100);
      }

      const docRef = await addDoc(collection(db, 'seasons'), {
        teamId:    currentTeam.id,
        coachId:   firebaseUser.uid,
        name:      form.name.trim(),
        status:    openNow ? 'open' : 'draft',

        // Core dates
        registrationOpen:  toSeasonDate(form.registrationOpen),
        registrationClose: toSeasonDate(form.registrationClose),
        seasonStart:       toSeasonDate(form.seasonStart),
        seasonEnd:         toSeasonDate(form.seasonEnd),
        maxPlayers:        form.maxPlayers ? Number(form.maxPlayers) : null,
        waivers: waivers
          .filter(w => w.name.trim() && w.text.trim())
          .map(({ name, text }) => ({ name: name.trim(), text: text.trim() })),
        waiverText: waivers[0]?.text?.trim() || '', // backward compat

        // Pricing
        registrationFee,
        pricingMode,
        divisionPricing,
        pricingTiers,

        // Early bird (always disabled when tiered — tiers replace it)
        earlyBirdEnabled:  pricingMode === 'tiered' ? false : ebOn,
        earlyBirdDiscount: pricingMode !== 'tiered' && ebOn ? Math.round(Number(earlyBirdDiscount) * 100) : null,
        earlyBirdDeadline: pricingMode !== 'tiered' && ebOn ? toSeasonDate(earlyBirdDeadline) : null,

        // Discounts
        siblingDiscountEnabled:  sibOn,
        siblingDiscountAmount:   sibOn  ? Math.round(Number(siblingAmount)    * 100) : null,
        returningDiscountEnabled: retOn,
        returningDiscountAmount:  retOn ? Math.round(Number(returningAmount) * 100) : null,

        // Financial aid
        financialAidEnabled: finAidOn,

        // Payment plans (Level 2 — auto-charge schedule)
        paymentPlanEnabled:      plOn,
        paymentPlanInstallments: plOn ? Number(planInstallments) : null,
        paymentPlanFrequency:    plOn ? planFrequency : null,
        paymentPlanChargeDay:    plOn && planFrequency === 'monthly' ? Number(planChargeDay) : null,

        // Processing fee pass-through ('absorb' | 'pass_to_parent')
        processingFeeMode,

        createdAt:  serverTimestamp(),
        updatedAt:  serverTimestamp(),
        whatsIncluded: feeIsFree ? [] : whatsIncluded.filter(s => s.trim()).map(s => s.trim()),
        requiredDocuments: requiredDocs,
      });

      toast.success(t('createSeason.toast.created'), { id: tid });
      track(ACTIVATION_EVENTS.SEASON_CREATED, {
        season_id: docRef.id,
        team_id: currentTeam.id,
        status: openNow ? 'open' : 'draft',
        pricing_mode: pricingMode,
        has_payment_plan: planEnabled,
      });
      navigate(`/seasons/${docRef.id}`);
    } catch (err) {
      console.error('[CreateSeason]', err);
      toast.error(err?.message || t('createSeason.toast.createFailed'), { id: tid });
      setSaving(false);
    }
  };

  /* ── Shared input class ─────────────────────────── */
  const inp = 'w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30';

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-8 border-b border-zinc-950/10 pb-4 dark:border-white/10">
        <Heading level={1}>{t('createSeason.title')}</Heading>
        <Text>{t('createSeason.subtitle')}</Text>
        {currentTeam?.name && <p className="mt-1 text-sm font-medium text-[#5a8a1f]">{currentTeam.name}</p>}
      </div>

      <form onSubmit={onSubmit} className="max-w-3xl space-y-10" noValidate>

        {/* ── Season Info ─────────────────────────────── */}
        <div className="space-y-6 border-b border-zinc-950/10 pb-8 dark:border-white/10">
          <div className="flex items-center gap-2">
            <CalendarDaysIcon className="h-5 w-5 text-[#5a8a1f]" />
            <Subheading level={2}>{t('createSeason.seasonInfo.title')}</Subheading>
          </div>

          <Field>
            <CatalystLabel htmlFor="name">{t('createSeason.seasonInfo.nameLabel')} <span className="text-red-500">*</span></CatalystLabel>
            <Input id="name" name="name" value={form.name} onChange={handleChange}
              placeholder={(() => {
                // Evergreen example: the audit caught a hardcoded "Spring
                // 2025" placeholder still showing in July 2026. Reuse the
                // auto-season name rule so the example is always current.
                const { key, year } = seasonAutoNameParts();
                return t(`createSeason.autoName.${key}`, { year });
              })()} invalid={!!(attempted && errors.name)} disabled={saving} />
            {attempted && errors.name
              ? <ErrorMessage>{errors.name}</ErrorMessage>
              : <Description>{t('createSeason.seasonInfo.nameDesc')}</Description>}
          </Field>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Field>
              <CatalystLabel htmlFor="seasonStart">{t('createSeason.seasonInfo.seasonStart')} <span className="text-red-500">*</span></CatalystLabel>
              <Input id="seasonStart" name="seasonStart" type="date" value={form.seasonStart}
                onChange={handleChange} invalid={!!(attempted && errors.seasonStart)} disabled={saving} />
              {attempted && errors.seasonStart && <ErrorMessage>{errors.seasonStart}</ErrorMessage>}
            </Field>
            <Field>
              <CatalystLabel htmlFor="seasonEnd">{t('createSeason.seasonInfo.seasonEnd')} <span className="text-red-500">*</span></CatalystLabel>
              <Input id="seasonEnd" name="seasonEnd" type="date" value={form.seasonEnd}
                onChange={handleChange} invalid={!!(attempted && errors.seasonEnd)} disabled={saving} />
              {attempted && errors.seasonEnd && <ErrorMessage>{errors.seasonEnd}</ErrorMessage>}
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Field>
              <CatalystLabel htmlFor="registrationOpen">{t('createSeason.seasonInfo.regOpens')} <span className="text-red-500">*</span></CatalystLabel>
              <Input id="registrationOpen" name="registrationOpen" type="date" value={form.registrationOpen}
                onChange={handleChange} invalid={!!(attempted && errors.registrationOpen)} disabled={saving} />
              {attempted && errors.registrationOpen && <ErrorMessage>{errors.registrationOpen}</ErrorMessage>}
            </Field>
            <Field>
              <CatalystLabel htmlFor="registrationClose">{t('createSeason.seasonInfo.regCloses')} <span className="text-red-500">*</span></CatalystLabel>
              <Input id="registrationClose" name="registrationClose" type="date" value={form.registrationClose}
                onChange={handleChange} invalid={!!(attempted && errors.registrationClose)} disabled={saving} />
              {attempted && errors.registrationClose && <ErrorMessage>{errors.registrationClose}</ErrorMessage>}
            </Field>
          </div>

          <Field>
            <CatalystLabel htmlFor="maxPlayers">{t('createSeason.seasonInfo.maxPlayers')} <span className="text-zinc-400 text-xs font-normal">{t('createSeason.seasonInfo.optional')}</span></CatalystLabel>
            <div className="relative max-w-xs">
              <UserGroupIcon className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 h-full w-9 text-zinc-400" />
              <Input id="maxPlayers" name="maxPlayers" type="number" min="1" step="1"
                value={form.maxPlayers} onChange={handleChange} placeholder="25"
                invalid={!!(attempted && errors.maxPlayers)} disabled={saving} className="pl-9" />
            </div>
            {attempted && errors.maxPlayers
              ? <ErrorMessage>{errors.maxPlayers}</ErrorMessage>
              : <Description>{t('createSeason.seasonInfo.maxPlayersDesc')}</Description>}
          </Field>
        </div>

        {/* ── What's Included ─────────────────────────────── */}
        {/* Hidden for free seasons (#5): this section shows parents what
            their FEE covers, which reads absurd when there is no fee. */}
        {!feeIsFree && (
        <div className="space-y-6 border-b border-zinc-950/10 pb-8 dark:border-white/10">
          <div className="flex items-center gap-2">
            <SparklesIcon className="h-5 w-5 text-[#5a8a1f]" />
            <Subheading level={2}>{t('createSeason.included.title')}</Subheading>
          </div>
          <Text className="!text-sm">
            {t('createSeason.included.desc')}
          </Text>
          <div className="space-y-2">
            {whatsIncluded.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-green-500 flex-shrink-0">✓</span>
                <input
                  type="text"
                  value={item}
                  onChange={e => updateIncludedItem(i, e.target.value)}
                  disabled={saving}
                  placeholder={t('createSeason.included.placeholder')}
                  className={clsx(inp, 'flex-1')}
                />
                {whatsIncluded.length > 1 && (
                  <button type="button" onClick={() => removeIncludedItem(i)}
                    className="p-1.5 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0">
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button type="button" onClick={addIncludedItem} disabled={saving}
            className="flex items-center gap-1.5 text-sm font-medium text-[#5a8a1f] hover:text-[#0d1f14] transition-colors">
            <PlusIcon className="h-4 w-4" />
            {t('createSeason.included.addItem')}
          </button>
        </div>
        )}

        {/* ── Pricing & Discounts ──────────────────────── */}
        <div className="space-y-8 border-b border-zinc-950/10 pb-8 dark:border-white/10">
          <div className="flex items-center gap-2">
            <TagIcon className="h-5 w-5 text-[#5a8a1f]" />
            <Subheading level={2}>{t('createSeason.pricing.title')}</Subheading>
          </div>

          {/* Pricing mode */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-zinc-700">{t('createSeason.pricing.mode')}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                { value: 'flat',      label: t('createSeason.pricing.flatLabel'),      desc: t('createSeason.pricing.flatDesc') },
                { value: 'divisions', label: t('createSeason.pricing.divisionsLabel'), desc: t('createSeason.pricing.divisionsDesc') },
                { value: 'tiered',    label: t('createSeason.pricing.tieredLabel'),    desc: t('createSeason.pricing.tieredDesc') },
              ].map(opt => (
                <label
                  key={opt.value}
                  className={clsx(
                    'flex items-start gap-3 rounded-xl border-2 p-4 cursor-pointer transition-colors',
                    pricingMode === opt.value
                      ? 'border-[#b5f03d] bg-[#b5f03d]/10'
                      : 'border-zinc-200 bg-white hover:border-zinc-300'
                  )}
                >
                  <input type="radio" name="pricingMode" value={opt.value}
                    checked={pricingMode === opt.value}
                    onChange={() => setPricingMode(opt.value)}
                    className="mt-0.5 accent-[#5a8a1f] focus:ring-[#b5f03d]/40" />
                  <div>
                    <p className="text-sm font-semibold text-zinc-900">{opt.label}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Flat fee */}
          {pricingMode === 'flat' && (
            <Field>
              <CatalystLabel htmlFor="registrationFee">{t('createSeason.pricing.regFee')} <span className="text-red-500">*</span></CatalystLabel>
              <div className="relative max-w-xs">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-zinc-400">$</span>
                <Input id="registrationFee" name="registrationFee" type="number" min="0" step="0.01"
                  value={form.registrationFee} onChange={handleChange} placeholder="150.00"
                  invalid={!!(attempted && errors.registrationFee)} disabled={saving} className="pl-7" />
              </div>
              {attempted && errors.registrationFee
                ? <ErrorMessage>{errors.registrationFee}</ErrorMessage>
                : <Description>{t('createSeason.pricing.regFeeDesc')}</Description>}
            </Field>
          )}

          {/* Division pricing */}
          {pricingMode === 'divisions' && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-zinc-700">{t('createSeason.pricing.divisions')}</p>
              {attempted && errors.divisions && (
                <p className="text-sm text-red-500">{errors.divisions}</p>
              )}
              <div className="space-y-2">
                {divisionRows.map((div, i) => (
                  <div key={div.id} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={div.name}
                      onChange={e => updateDivision(div.id, 'name', e.target.value)}
                      placeholder={t('createSeason.pricing.divisionPlaceholder', { n: 8 + i * 2 })}
                      disabled={saving}
                      className={clsx(inp, 'flex-1')}
                    />
                    <div className="relative w-32 shrink-0">
                      <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-zinc-400 text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={div.fee}
                        onChange={e => updateDivision(div.id, 'fee', e.target.value)}
                        placeholder="0.00"
                        disabled={saving}
                        className={clsx(inp, 'pl-6')}
                      />
                    </div>
                    {divisionRows.length > 1 && (
                      <button type="button" onClick={() => removeDivision(div.id)}
                        className="p-1.5 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" onClick={addDivision} disabled={saving}
                className="flex items-center gap-1.5 text-sm font-medium text-[#5a8a1f] hover:text-[#0d1f14] transition-colors">
                <PlusIcon className="h-4 w-4" />
                {t('createSeason.pricing.addDivision')}
              </button>
            </div>
          )}

          {/* Tiered pricing */}
          {pricingMode === 'tiered' && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-zinc-700">{t('createSeason.pricing.tiers')}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {t('createSeason.pricing.tiersDesc')}
                  </p>
                </div>
                <button type="button" onClick={() => setCalcOpen(v => !v)} disabled={saving}
                  className="flex items-center gap-1.5 rounded-lg border border-[#b5f03d]/40 bg-[#b5f03d]/10 px-3 py-1.5 text-xs font-medium text-[#0d1f14] hover:bg-[#b5f03d]/20 transition-colors flex-shrink-0">
                  <SparklesIcon className="h-3.5 w-3.5" />
                  {calcOpen ? t('createSeason.pricing.hideCalculator') : t('createSeason.pricing.helpSetPrices')}
                </button>
              </div>

              {/* Calculator panel */}
              {calcOpen && (
                <div className="rounded-xl border border-[#b5f03d]/40 bg-[#b5f03d]/5 p-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-[#0d1f14]">{t('createSeason.pricing.prorationCalc')}</p>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      {t('createSeason.pricing.prorationDesc')}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Field>
                      <CatalystLabel>{t('createSeason.pricing.fullSeasonPrice')}</CatalystLabel>
                      <div className="relative">
                        <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-zinc-400 text-sm">$</span>
                        <input type="number" min="0" step="0.01" value={calcFullPrice}
                          onChange={e => setCalcFullPrice(e.target.value)} placeholder="2800.00"
                          disabled={saving} className={clsx(inp, 'pl-6')} />
                      </div>
                    </Field>
                    <Field>
                      <CatalystLabel>{t('createSeason.pricing.numTiers')}</CatalystLabel>
                      <select value={calcTierCount}
                        onChange={e => setCalcTierCount(e.target.value)}
                        disabled={saving} className={inp}>
                        {[2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </Field>
                    <div className="flex items-end">
                      <Button type="button" onClick={applyCalculator} disabled={saving} className="w-full">
                        {t('createSeason.pricing.generateTiers')}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-zinc-500">
                    {t('createSeason.pricing.prorationTip')}
                  </p>
                </div>
              )}

              {attempted && errors.tiers && <ErrorMessage>{errors.tiers}</ErrorMessage>}

              {/* Tier rows */}
              <div className="space-y-3">
                {tierRows.map((tierRow, i) => (
                  <div key={tierRow.id} className="rounded-xl border border-zinc-200 bg-white p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="flex-shrink-0 h-6 w-6 rounded-full bg-[#b5f03d]/20 text-[#0d1f14] flex items-center justify-center text-xs font-bold">{i + 1}</span>
                      <input
                        type="text"
                        value={tierRow.name}
                        onChange={e => updateTier(tierRow.id, 'name', e.target.value)}
                        disabled={saving}
                        placeholder={t('createSeason.pricing.tierNamePlaceholder')}
                        className="flex-1 text-sm font-semibold bg-transparent focus:outline-none text-zinc-900 placeholder:text-zinc-400 placeholder:font-normal"
                      />
                      {tierRow.price !== '' && !isNaN(Number(tierRow.price)) && (
                        <span className="text-xs font-semibold text-[#5a8a1f]">
                          {formatTierPrice(Math.round(Number(tierRow.price) * 100))}
                        </span>
                      )}
                      {tierRows.length > 1 && (
                        <button type="button" onClick={() => removeTier(tierRow.id)} disabled={saving}
                          className="p-1 rounded text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field>
                        <CatalystLabel>{t('createSeason.pricing.price')}</CatalystLabel>
                        <div className="relative">
                          <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-zinc-400 text-sm">$</span>
                          <input type="number" min="0" step="0.01" value={tierRow.price}
                            onChange={e => updateTier(tierRow.id, 'price', e.target.value)}
                            placeholder="0.00" disabled={saving}
                            className={clsx(inp, 'pl-6')} />
                        </div>
                      </Field>
                      <Field>
                        <CatalystLabel>{t('createSeason.pricing.cap')} <span className="text-zinc-400 text-xs font-normal">{t('createSeason.seasonInfo.optional')}</span></CatalystLabel>
                        <input type="number" min="1" step="1" value={tierRow.maxRegistrations}
                          onChange={e => updateTier(tierRow.id, 'maxRegistrations', e.target.value)}
                          placeholder={t('createSeason.pricing.noLimit')} disabled={saving} className={inp} />
                      </Field>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field>
                        <CatalystLabel>{t('createSeason.pricing.starts')}</CatalystLabel>
                        <input type="date" value={tierRow.startsAt}
                          onChange={e => updateTier(tierRow.id, 'startsAt', e.target.value)}
                          disabled={saving} className={inp} />
                      </Field>
                      <Field>
                        <CatalystLabel>{t('createSeason.pricing.ends')}</CatalystLabel>
                        <input type="date" value={tierRow.endsAt}
                          onChange={e => updateTier(tierRow.id, 'endsAt', e.target.value)}
                          disabled={saving} className={inp} />
                      </Field>
                    </div>
                  </div>
                ))}
              </div>

              <button type="button" onClick={addTier} disabled={saving}
                className="flex items-center gap-1.5 text-sm font-medium text-[#5a8a1f] hover:text-[#0d1f14] transition-colors">
                <PlusIcon className="h-4 w-4" />
                {t('createSeason.pricing.addTier')}
              </button>
            </div>
          )}

          {/* Money extras (#5): everything from early bird through financial
              aid is meaningless on a free season. One clean hide. */}
          {!feeIsFree && (<>
          {/* Early bird — hidden when tiered mode is active (tiers ARE the time-based discount) */}
          {pricingMode !== 'tiered' && (
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-5 space-y-4">
            <Toggle
              enabled={earlyBirdEnabled}
              onToggle={() => setEarlyBirdEnabled(v => !v)}
              label={t('createSeason.earlyBird.label')}
              description={t('createSeason.earlyBird.desc')}
            />
            {earlyBirdEnabled && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 pt-2 border-t border-zinc-200 mt-2">
                <Field>
                  <CatalystLabel>{t('createSeason.earlyBird.discountAmount')} <span className="text-red-500">*</span></CatalystLabel>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-zinc-400 text-sm">$</span>
                    <input type="number" min="1" step="0.01" value={earlyBirdDiscount}
                      onChange={e => setEarlyBirdDiscount(e.target.value)} placeholder="20.00"
                      disabled={saving} className={clsx(inp, 'pl-6',
                        attempted && errors.earlyBirdDiscount ? 'border-red-500' : '')} />
                  </div>
                  {attempted && errors.earlyBirdDiscount && <ErrorMessage>{errors.earlyBirdDiscount}</ErrorMessage>}
                  <Description>{t('createSeason.earlyBird.discountDesc')}</Description>
                </Field>
                <Field>
                  <CatalystLabel>{t('createSeason.earlyBird.deadline')} <span className="text-red-500">*</span></CatalystLabel>
                  <input type="date" value={earlyBirdDeadline}
                    onChange={e => setEarlyBirdDeadline(e.target.value)} disabled={saving}
                    className={clsx(inp, attempted && errors.earlyBirdDeadline ? 'border-red-500' : '')} />
                  {attempted && errors.earlyBirdDeadline && <ErrorMessage>{errors.earlyBirdDeadline}</ErrorMessage>}
                </Field>
              </div>
            )}
          </div>
          )}

          {/* Sibling discount */}
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-5 space-y-4">
            <Toggle
              enabled={siblingEnabled}
              onToggle={() => setSiblingEnabled(v => !v)}
              label={t('createSeason.sibling.label')}
              description={t('createSeason.sibling.desc')}
            />
            {siblingEnabled && (
              <div className="pt-2 border-t border-zinc-200">
                <Field>
                  <CatalystLabel>{t('createSeason.sibling.perLabel')} <span className="text-red-500">*</span></CatalystLabel>
                  <div className="relative max-w-xs">
                    <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-zinc-400 text-sm">$</span>
                    <input type="number" min="1" step="0.01" value={siblingAmount}
                      onChange={e => setSiblingAmount(e.target.value)} placeholder="20.00"
                      disabled={saving} className={clsx(inp, 'pl-6',
                        attempted && errors.siblingAmount ? 'border-red-500' : '')} />
                  </div>
                  {attempted && errors.siblingAmount && <ErrorMessage>{errors.siblingAmount}</ErrorMessage>}
                </Field>
              </div>
            )}
          </div>

          {/* Returning player discount */}
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-5 space-y-4">
            <Toggle
              enabled={returningEnabled}
              onToggle={() => setReturningEnabled(v => !v)}
              label={t('createSeason.returning.label')}
              description={t('createSeason.returning.desc')}
            />
            {returningEnabled && (
              <div className="pt-2 border-t border-zinc-200">
                <Field>
                  <CatalystLabel>{t('createSeason.returning.amountLabel')} <span className="text-red-500">*</span></CatalystLabel>
                  <div className="relative max-w-xs">
                    <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-zinc-400 text-sm">$</span>
                    <input type="number" min="1" step="0.01" value={returningAmount}
                      onChange={e => setReturningAmount(e.target.value)} placeholder="15.00"
                      disabled={saving} className={clsx(inp, 'pl-6',
                        attempted && errors.returningAmount ? 'border-red-500' : '')} />
                  </div>
                  {attempted && errors.returningAmount && <ErrorMessage>{errors.returningAmount}</ErrorMessage>}
                </Field>
              </div>
            )}
          </div>

          {/* Payment plans */}
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-5 space-y-4">
            <Toggle
              enabled={planEnabled}
              onToggle={() => setPlanEnabled(v => !v)}
              label={t('createSeason.plan.label')}
              description={t('createSeason.plan.desc')}
            />
            {planEnabled && (
              <div className="space-y-4 pt-2 border-t border-zinc-200">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field>
                    <CatalystLabel>{t('createSeason.plan.installments')}</CatalystLabel>
                    <input
                      type="number"
                      min="2"
                      max="12"
                      step="1"
                      value={planInstallments}
                      onChange={e => setPlanInstallments(e.target.value)}
                      disabled={saving}
                      className={clsx(inp, attempted && errors.planInstallments ? 'border-red-500' : '')}
                    />
                    {attempted && errors.planInstallments
                      ? <ErrorMessage>{errors.planInstallments}</ErrorMessage>
                      : <Description>{t('createSeason.plan.installmentsDesc')}</Description>}
                  </Field>

                  <Field>
                    <CatalystLabel>{t('createSeason.plan.frequency')}</CatalystLabel>
                    <select
                      value={planFrequency}
                      onChange={e => setPlanFrequency(e.target.value)}
                      disabled={saving}
                      className={inp}
                    >
                      <option value="monthly">{t('createSeason.plan.freqMonthly')}</option>
                      <option value="biweekly">{t('createSeason.plan.freqBiweekly')}</option>
                      <option value="weekly">{t('createSeason.plan.freqWeekly')}</option>
                    </select>
                  </Field>
                </div>

                {planFrequency === 'monthly' && (
                  <Field>
                    <CatalystLabel>{t('createSeason.plan.chargeDay')}</CatalystLabel>
                    <div className="max-w-xs">
                      <input
                        type="number"
                        min="1"
                        max="28"
                        step="1"
                        value={planChargeDay}
                        onChange={e => setPlanChargeDay(e.target.value)}
                        disabled={saving}
                        className={clsx(inp, attempted && errors.planChargeDay ? 'border-red-500' : '')}
                      />
                    </div>
                    {attempted && errors.planChargeDay
                      ? <ErrorMessage>{errors.planChargeDay}</ErrorMessage>
                      : <Description>{t('createSeason.plan.chargeDayDesc')}</Description>}
                  </Field>
                )}

                <div className="rounded-lg bg-[#b5f03d]/10 border border-[#b5f03d]/30 px-4 py-3 text-xs text-[#0d1f14]">
                  <p className="font-semibold mb-1">{t('createSeason.plan.howItWorks')}</p>
                  <p>
                    {t('createSeason.plan.howItWorksDesc')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Processing fees */}
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-5 space-y-4">
            <div>
              <p className="text-sm font-semibold text-zinc-800">{t('createSeason.processing.title')}</p>
              <p className="text-xs text-zinc-500 mt-0.5">
                {t('createSeason.processing.desc')}
              </p>
            </div>
            <div className="space-y-2">
              <label className={clsx(
                'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                processingFeeMode === 'pass_to_parent'
                  ? 'border-[#b5f03d] bg-[#b5f03d]/10'
                  : 'border-zinc-200 bg-white hover:border-zinc-300'
              )}>
                <input
                  type="radio"
                  name="processingFeeMode"
                  value="pass_to_parent"
                  checked={processingFeeMode === 'pass_to_parent'}
                  onChange={() => setProcessingFeeMode('pass_to_parent')}
                  className="mt-0.5 h-4 w-4 accent-[#5a8a1f] focus:ring-[#b5f03d]/40"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-zinc-800">
                    {t('createSeason.processing.parentPaysTitle')} <span className="text-xs font-normal text-[#5a8a1f]">{t('createSeason.processing.recommended')}</span>
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {t('createSeason.processing.parentPaysDesc')}
                  </p>
                </div>
              </label>
              <label className={clsx(
                'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                processingFeeMode === 'absorb'
                  ? 'border-[#b5f03d] bg-[#b5f03d]/10'
                  : 'border-zinc-200 bg-white hover:border-zinc-300'
              )}>
                <input
                  type="radio"
                  name="processingFeeMode"
                  value="absorb"
                  checked={processingFeeMode === 'absorb'}
                  onChange={() => setProcessingFeeMode('absorb')}
                  className="mt-0.5 h-4 w-4 accent-[#5a8a1f] focus:ring-[#b5f03d]/40"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-zinc-800">{t('createSeason.processing.absorbTitle')}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {t('createSeason.processing.absorbDesc')}
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Financial aid */}
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-5">
            <Toggle
              enabled={aidEnabled}
              onToggle={() => setAidEnabled(v => !v)}
              label={t('createSeason.aid.label')}
              description={t('createSeason.aid.desc')}
            />
          </div>
          </>)}

          {feeIsFree && (
            <div className="rounded-xl border border-[#b5f03d]/40 bg-[#b5f03d]/10 px-4 py-3 text-sm text-[#0d1f14]">
              {t('createSeason.pricing.freeNote')}
            </div>
          )}
        </div>

        {/* ── Waivers ─────────────────────────────────── */}
        <div className="space-y-6 border-b border-zinc-950/10 pb-8 dark:border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-[#5a8a1f]" />
              <Subheading level={2}>{t('createSeason.waivers.title')}</Subheading>
            </div>
            <span className="text-xs text-zinc-400">{t('createSeason.waivers.count', { count: waivers.length })}</span>
          </div>
          <Text className="!text-sm">{t('createSeason.waivers.desc')}</Text>

          {/* Legal disclaimer — Scrimly is not responsible for waiver content */}
          <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-bold mb-1">{t('createSeason.waivers.warnTitle')}</p>
                <p className="leading-relaxed">
                  {t('createSeason.waivers.warnBody')}
                </p>
              </div>
            </div>
          </div>

          {attempted && errors.waivers && <ErrorMessage>{errors.waivers}</ErrorMessage>}

          <div className="space-y-4">
            {waivers.map((w, i) => (
              <div key={w.id} className="rounded-xl border border-zinc-200 bg-zinc-50 overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-200 bg-white">
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-[#b5f03d]/20 text-[#0d1f14] flex items-center justify-center text-xs font-bold">{i + 1}</span>
                  <input
                    type="text"
                    value={w.name}
                    onChange={e => updateWaiver(w.id, 'name', e.target.value)}
                    disabled={saving}
                    placeholder={t('createSeason.waivers.namePlaceholder')}
                    className="flex-1 text-sm font-semibold bg-transparent focus:outline-none text-zinc-900 placeholder:text-zinc-400 placeholder:font-normal"
                  />
                  {waivers.length > 1 && (
                    <button type="button" onClick={() => removeWaiver(w.id)} disabled={saving}
                      className="p-1 rounded text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="p-4">
                  <textarea
                    rows={i === 0 ? 8 : 5}
                    value={w.text}
                    onChange={e => updateWaiver(w.id, 'text', e.target.value)}
                    disabled={saving}
                    placeholder={t('createSeason.waivers.textPlaceholder')}
                    className={clsx(inp, 'resize-none leading-relaxed')}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Add waiver buttons */}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => addWaiver()} disabled={saving}
              className="flex items-center gap-1.5 text-sm font-medium text-[#5a8a1f] hover:text-[#0d1f14] transition-colors">
              <PlusIcon className="h-4 w-4" />
              {t('createSeason.waivers.add')}
            </button>
            <span className="text-zinc-300 text-sm">|</span>
            {[
              { key: 'Liability Waiver',    labelKey: 'liability' },
              { key: 'Concussion Protocol', labelKey: 'concussion' },
              { key: 'Social Media Policy', labelKey: 'social' },
              { key: 'Photo Release',       labelKey: 'photo' },
            ].map(preset => (
              <button key={preset.key} type="button" onClick={() => addWaiver(preset.key)} disabled={saving}
                className="text-xs text-zinc-500 hover:text-zinc-700 underline transition-colors">
                + {t(`createSeason.waivers.presets.${preset.labelKey}`)}
              </button>
            ))}
          </div>
        </div>

        {/* ── Required Documents ──────────────────────── */}
        <div className="space-y-6 border-b border-zinc-950/10 pb-8 dark:border-white/10">
          <div className="flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5 text-[#5a8a1f]" />
            <Subheading level={2}>{t('createSeason.docs.title')}</Subheading>
          </div>
          <Text className="!text-sm">
            {t('createSeason.docs.desc')}
          </Text>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {DOC_OPTIONS.map(opt => {
              const checked = requiredDocs.includes(opt.key);
              return (
                <label
                  key={opt.key}
                  className={clsx(
                    'flex items-start gap-3 rounded-xl border-2 p-4 cursor-pointer transition-colors select-none',
                    checked ? 'border-[#b5f03d] bg-[#b5f03d]/10' : 'border-zinc-200 bg-white hover:border-zinc-300'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleDoc(opt.key)}
                    disabled={saving}
                    className="mt-0.5 h-4 w-4 rounded border-zinc-300 accent-[#5a8a1f] focus:ring-[#b5f03d]/40"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-900">
                      <span className="mr-1.5">{opt.icon}</span>{opt.label}
                    </p>
                    <p className="text-xs text-zinc-500 mt-0.5">{opt.hint}</p>
                  </div>
                </label>
              );
            })}
          </div>

          {requiredDocs.length > 0 && (
            <p className="text-xs text-zinc-500 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              {t('createSeason.docs.uploadNote')}
            </p>
          )}
        </div>

        {/* ── Open Now toggle ─────────────────────────── */}
        <div className="rounded-xl border border-[#b5f03d]/30 bg-[#b5f03d]/10 px-5 py-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={openNow} onChange={e => setOpenNow(e.target.checked)}
              disabled={saving}
              className="mt-0.5 h-4 w-4 rounded border-zinc-300 accent-[#5a8a1f] focus:ring-[#b5f03d]/40" />
            <div>
              <p className="text-sm font-semibold text-[#0d1f14]">{t('createSeason.openNow.label')}</p>
              <p className="mt-0.5 text-xs text-zinc-600">
                {t('createSeason.openNow.desc')}
              </p>
            </div>
          </label>
        </div>

        {/* ── Submit ──────────────────────────────────── */}
        <div className="pt-4 flex items-center justify-between gap-4">
          <Button type="button" plain onClick={() => navigate(-1)} disabled={saving}>{t('createSeason.cancel')}</Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <><ArrowPathIcon className="size-4 animate-spin mr-1.5" />{t('createSeason.submit.creating')}</>
            ) : (
              <><PlusIcon className="size-4 mr-1.5" />{openNow ? t('createSeason.submit.createOpen') : t('createSeason.submit.createDraft')}</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
